/**
 * NewsletterSection — Email signup block for the homepage.
 *
 * Calls a server function that subscribes the email to Shopify email
 * marketing through the Admin API.
 */
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { Check, CircleCheck } from "lucide-react";
import { subscribeToNewsletter } from "@modules/shop/newsletter.api";
import { Button } from "@ui/components/ui/button";
import { Input } from "@ui/components/ui/input";
import type { FormEvent } from "react";

const points = ["point1", "point2", "point3"] as const;

export function NewsletterSection({ locale }: Readonly<{ locale: string }>) {
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = inputRef.current?.value.trim();
    if (!email) return;

    setIsPending(true);
    try {
      const result = await subscribeToNewsletter({ data: { email } });

      if (result.success) {
        setIsSubscribed(true);
        toast.success(t("Homepage.signUpToNewsletter.subscriptionSuccess"), {
          description: t("Homepage.signUpToNewsletter.subscriptionMessage"),
          position: "bottom-center",
        });
        if (inputRef.current) inputRef.current.value = "";
      } else {
        toast.error(t("Homepage.signUpToNewsletter.subscriptionError"), {
          position: "bottom-center",
        });
      }
    } catch {
      toast.error(t("Homepage.signUpToNewsletter.subscriptionError"), {
        position: "bottom-center",
      });
    } finally {
      setIsPending(false);
    }
  }

  return (
    <section className="px-4 pb-16 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-8 rounded-2xl border border-border-5 bg-background-7 p-8 sm:p-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:items-center lg:gap-16 lg:p-12">
        <div>
          <h2 className="text-xlarge font-bold text-balance text-text-1">
            {t("Homepage.signUpToNewsletter.title")}
          </h2>
          <ul className="mt-5 space-y-2.5 text-text-2">
            {points.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-pretty">
                <Check
                  className="mt-1 size-4 shrink-0 text-primary"
                  strokeWidth={2}
                  aria-hidden
                />
                {t(`Homepage.signUpToNewsletter.${point}`)}
              </li>
            ))}
          </ul>
        </div>

        <div aria-live="polite">
          {isSubscribed ? (
            <p className="flex items-start gap-3 rounded-xl bg-background-6 p-5 text-text-2 text-pretty">
              <CircleCheck
                className="mt-0.5 size-5 shrink-0 text-primary"
                strokeWidth={2}
                aria-hidden
              />
              {t("Homepage.signUpToNewsletter.subscriptionMessage")}
            </p>
          ) : (
            <form onSubmit={handleSubmit}>
              <label htmlFor={inputId} className="sr-only">
                {t("Homepage.signUpToNewsletter.inputLabel")}
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  ref={inputRef}
                  id={inputId}
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  disabled={isPending}
                  variant="box"
                  placeholder={t(
                    "Homepage.signUpToNewsletter.inputPlaceholder",
                  )}
                  wrapperClassName="flex-1 bg-background-7"
                />
                <Button
                  type="submit"
                  primary="default"
                  size="normal"
                  disabled={isPending}
                  className="shrink-0 font-semibold"
                >
                  {isPending ? "…" : t("Homepage.signUpToNewsletter.button")}
                </Button>
              </div>
              <p className="mt-3 text-small text-text-3 text-pretty">
                {t("Homepage.signUpToNewsletter.consentNote")}{" "}
                <Link
                  to="/$locale/info/security-and-privacy-policy"
                  params={{ locale }}
                  className="text-primary underline underline-offset-2 hover:text-primary-darker1"
                >
                  {t("Homepage.signUpToNewsletter.privacyLink")}
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
